import type { NavigateFunction } from 'react-router';
import logger from '@ayocore/platform/common/helpers/logger';
import type { AwaitableRpcClient } from '@ayocore/platform/server/rpc/types';
import { extractErrorMessage } from '@ayocore/platform/client/utils';
import type { LeadRepository } from '../../../../server/repositories/LeadRepository';
import type { PartnerRepository } from '../../../../server/repositories/PartnerRepository';
import { assistant } from '@ayocore/llm/client/assistant';
import type { Lead, UpdateLead } from '../../../../schemas/lead.schema';
import type { Partner } from '../../../../schemas/partner.schema';
import type { ContactPerson } from '../../../../schemas/contact-person.schema';
import type { ContactChannel } from '../../../../schemas/contact-channel.schema';
import type { CompanyInfo } from '@ayocore/erp/crm/common/types/company';

/**
 * Расширенный тип лида для безопасного рендеринга relation-объектов
 */
export type LeadExtended = Lead & {
  lead_type?: { id: number; slug?: string; name?: string } | null;
  lead_status?: { name?: string; color?: string; is_negative?: boolean } | null;
  manager?: { id?: number; name?: string; email?: string } | null;
  lost_reason?: { id: number; name?: string } | null;
  created_by?: { id?: number; name?: string } | null;
  updated_by?: { id?: number; name?: string } | null;
  lead_source?: { id: number; name?: string; code?: string } | null;
  sales_area?: { id: number; name?: string } | null;
  deal?: {
    id: number;
    number?: string | null;
    created_at?: string | Date | null;
  } | null;
};

/**
 * Состояние контроллера LeadView
 */
export interface LeadViewState {
  lead: LeadExtended | null;
  partner: Partner | null;
  contactPersons: ContactPerson[];
  contactChannels: ContactChannel[];
  loading: boolean;
  error: string | null;
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    loading: boolean;
  };
  modals: {
    edit: boolean;
    delete: boolean;
  };
}

/**
 * LeadViewController - контроллер для управления состоянием LeadView
 *
 * Следует паттерну NotificationController:
 * - Внешнее состояние (не React state)
 * - Простая система подписки через колбэки
 * - Методы subscribe и getSnapshot для useSyncExternalStore
 */
export class LeadViewController {
  // Внешнее состояние (не React state)
  private state: LeadViewState = {
    lead: null,
    partner: null,
    contactPersons: [],
    contactChannels: [],
    loading: true,
    error: null,
    permissions: {
      canEdit: false,
      canDelete: false,
      loading: true,
    },
    modals: {
      edit: false,
      delete: false,
    },
  };

  // Система подписки через колбэки
  private listeners: Set<() => void> = new Set();

  // Подписка для useSyncExternalStore
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // Получить снимок текущего состояния (для useSyncExternalStore)
  // Важно: useSyncExternalStore требует референциальной стабильности снапшота,
  // поэтому возвращаем тот же объект состояния без копирования.
  getSnapshot = (): LeadViewState => this.state;

  // Уведомить всех подписчиков об изменении состояния
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        logger.error('Error in LeadViewController listener:', error);
      }
    });
  }

  // Вспомогательный метод: неизменяемое обновление состояния
  private replaceState(patch: Partial<LeadViewState>): void {
    this.state = { ...this.state, ...patch };
  }

  /**
   * Загрузить данные лида
   */
  async load(
    leadId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    leadRepository: AwaitableRpcClient<LeadRepository>,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    if (!leadId || isNaN(leadId)) {
      this.replaceState({
        error: 'Неверный ID лида',
        loading: false,
      });
      this.notifyListeners();
      return;
    }

    try {
      this.replaceState({
        loading: true,
        error: null,
      });
      this.notifyListeners();

      // Загружаем данные лида со связями (статус/тип/менеджер/created_by/updated_by)
      const leadResult = await leadRepository.withRelations().findById(leadId);

      if (!leadResult) {
        this.replaceState({
          error: `Лид с ID ${leadId} не найден`,
          loading: false,
        });
        if (!isModal) {
          setTabTitle(`Лид #${leadId} - Не найден`);
        }
        this.notifyListeners();
        return;
      }

      const leadExtended = leadResult as LeadExtended;

      // Загружаем связанные данные
      const [partnerResult, contactPersonsResult, contactChannelsResult] = await Promise.all([
        leadExtended.partner_id
          ? partnerRepository.findById(leadExtended.partner_id)
          : Promise.resolve(null),
        leadRepository.getLeadContactPersons(leadId),
        leadRepository.getLeadContacts(leadId),
      ]);

      this.replaceState({
        lead: leadExtended,
        partner: partnerResult,
        contactPersons: contactPersonsResult || [],
        contactChannels: contactChannelsResult || [],
        loading: false,
      });

      // Устанавливаем заголовок вкладки только если не в модалке
      if (!isModal) {
        setTabTitle(`Лид ${leadExtended.title}`);
      }

      // Отправляем UI Context для LLM ассистента
      try {
        const entity = await leadRepository.getEntity(leadId);
        if (entity) {
          await assistant.setEntityContext(entity);
          logger.debug('🖥️ [LeadViewController] UI Context set', { entity, leadId });
        }
      } catch (err) {
        // Игнорируем ошибку - UI Context не критичен
        logger.debug('⚠️ [LeadViewController] Failed to set UI context:', err);
      }

      this.notifyListeners();
    } catch (err) {
      logger.error('Failed to fetch lead data:', err);
      this.replaceState({
        error: 'Не удалось загрузить данные лида',
        loading: false,
      });
      if (!isModal) {
        setTabTitle(`Лид #${leadId} - Ошибка`);
      }
      this.notifyListeners();
    }
  }

  /**
   * Проверить права доступа для текущего лида
   */
  async checkPermissions(
    leadId: number,
    leadRepository: AwaitableRpcClient<LeadRepository>
  ): Promise<void> {
    if (!leadId || isNaN(leadId)) {
      this.replaceState({
        permissions: {
          canEdit: false,
          canDelete: false,
          loading: false,
        },
      });
      this.notifyListeners();
      return;
    }

    try {
      this.replaceState({
        permissions: {
          ...this.state.permissions,
          loading: true,
        },
      });
      this.notifyListeners();

      // Проверяем права на редактирование и удаление
      const [canUpdateResult, canDeleteResult] = await Promise.all([
        leadRepository.canUpdate(leadId),
        leadRepository.canDelete(leadId),
      ]);

      this.replaceState({
        permissions: {
          canEdit: canUpdateResult.allowed,
          canDelete: canDeleteResult.allowed,
          loading: false,
        },
      });
      this.notifyListeners();
    } catch (err) {
      logger.error('Failed to check permissions:', err);
      // При ошибке запрещаем действия (fail-secure)
      this.replaceState({
        permissions: {
          canEdit: false,
          canDelete: false,
          loading: false,
        },
      });
      this.notifyListeners();
    }
  }

  /**
   * Обновить контактные каналы
   */
  async refreshContacts(
    leadId: number,
    leadRepository: AwaitableRpcClient<LeadRepository>
  ): Promise<void> {
    if (!leadId || isNaN(leadId)) {
      return;
    }

    try {
      logger.info('🔄 [LeadViewController] Refreshing channels for lead:', leadId);

      const contactChannelsResult = await leadRepository.getLeadContacts(leadId);
      logger.info('📋 [LeadViewController] Updated channels:', contactChannelsResult);

      this.replaceState({
        contactChannels: contactChannelsResult || [],
      });
      this.notifyListeners();
    } catch (error) {
      logger.error('❌ [LeadViewController] Error refreshing channels:', error);
    }
  }

  /**
   * Обновить контактные лица
   */
  async refreshContactPersons(
    leadId: number,
    leadRepository: AwaitableRpcClient<LeadRepository>
  ): Promise<void> {
    if (!leadId || isNaN(leadId)) {
      return;
    }

    try {
      logger.info('🔄 [LeadViewController] Refreshing contact persons for lead:', leadId);

      const contactPersonsResult = await leadRepository.getLeadContactPersons(leadId);
      logger.info('📋 [LeadViewController] Updated contact persons:', contactPersonsResult);

      this.replaceState({
        contactPersons: contactPersonsResult || [],
      });
      this.notifyListeners();
    } catch (error) {
      logger.error('❌ [LeadViewController] Error refreshing contact persons:', error);
    }
  }

  /**
   * Универсальное обновление поля лида (оптимистично).
   * Для partner_id и manager_id — обновляет lead и partner без loading (как refreshPartner в PartnerView).
   */
  async updateLeadField<K extends keyof UpdateLead>(
    leadId: number,
    field: K,
    value: UpdateLead[K],
    leadRepository: AwaitableRpcClient<LeadRepository>,
    partnerRepository?: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    const currentLead = this.state.lead;
    if (!currentLead) return;

    if (field === 'title') {
      const nextTitle = String(value ?? '').trim();
      if (!nextTitle) {
        this.replaceState({ error: 'Название лида не может быть пустым' });
        this.notifyListeners();
        return;
      }
      value = nextTitle as UpdateLead[K];
    }

    const prevLead = currentLead;
    this.replaceState({ lead: { ...currentLead, [field]: value } as LeadExtended, error: null });
    this.notifyListeners();

    try {
      await leadRepository.update(leadId, { [field]: value } as UpdateLead);

      if ((field === 'partner_id' || field === 'manager_id') && partnerRepository) {
        const partnerId =
          field === 'partner_id' ? (value as number | null) : (this.state.lead?.partner_id ?? null);
        const [leadResult, partnerResult] = await Promise.all([
          leadRepository.withRelations().findById(leadId),
          partnerId ? partnerRepository.findById(partnerId) : Promise.resolve(null),
        ]);
        if (leadResult) {
          this.replaceState({
            lead: leadResult as LeadExtended,
            partner: partnerResult as Partner | null,
            error: null,
          });
        }
      } else {
        const updatedLead = await leadRepository.withRelations().findById(leadId);
        if (updatedLead) {
          this.replaceState({ lead: updatedLead as LeadExtended, error: null });
        }
      }
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to update lead field:', error);
      this.replaceState({
        lead: prevLead,
        error: 'Не удалось обновить поле лида',
      });
      this.notifyListeners();
    }
  }

  /**
   * Привязать существующего партнёра к лиду.
   */
  async attachPartnerToLead(
    leadId: number,
    partnerId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    leadRepository: AwaitableRpcClient<LeadRepository>,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    try {
      this.replaceState({ loading: true, error: null });
      this.notifyListeners();

      await leadRepository.update(leadId, { partner_id: partnerId });
      await this.load(
        leadId,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      );
    } catch (error) {
      logger.error('Failed to attach partner to lead:', error);
      this.replaceState({
        error: 'Не удалось привязать партнёра к лиду',
        loading: false,
      });
      this.notifyListeners();
    }
  }

  /**
   * Создать партнёра из внешнего источника и привязать к лиду.
   */
  async createPartnerFromExternal(
    leadId: number,
    company: CompanyInfo,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    leadRepository: AwaitableRpcClient<LeadRepository>,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    if (!company?.name) {
      this.replaceState({
        error: 'Не удалось создать партнёра: отсутствует название компании',
      });
      this.notifyListeners();
      return;
    }

    try {
      this.replaceState({ loading: true, error: null });
      this.notifyListeners();

      const partner = await partnerRepository.createExtended({
        name: company.name,
        full_name: company.full_name || null,
        inn: company.inn || null,
        ogrn: company.ogrn || null,
        address: company.address || null,
        director: company.director || null,
        notes: null,
        manager_id: null,
        customer_status_id: null,
        sales_area_id: null,
        linked_id: null,
        first_contact_date: null,
        last_contact_date: null,
        next_contact_date: null,
      });

      await this.attachPartnerToLead(
        leadId,
        partner.id,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      );
    } catch (error) {
      logger.error('Failed to create partner from external source:', error);
      this.replaceState({
        error: 'Не удалось создать партнёра из внешнего источника',
        loading: false,
      });
      this.notifyListeners();
    }
  }

  /**
   * Открыть модалку редактирования
   */
  openEdit(): void {
    this.replaceState({
      modals: {
        ...this.state.modals,
        edit: true,
      },
    });
    this.notifyListeners();
  }

  /**
   * Закрыть модалку редактирования
   */
  closeEdit(): void {
    this.replaceState({
      modals: {
        ...this.state.modals,
        edit: false,
      },
    });
    this.notifyListeners();
  }

  /**
   * Открыть модалку удаления
   */
  openDelete(): void {
    this.replaceState({
      modals: {
        ...this.state.modals,
        delete: true,
      },
    });
    this.notifyListeners();
  }

  /**
   * Закрыть модалку удаления
   */
  closeDelete(): void {
    this.replaceState({
      modals: {
        ...this.state.modals,
        delete: false,
      },
    });
    this.notifyListeners();
  }

  /**
   * Удалить лид и перенаправить на список лидов
   */
  async deleteLead(
    leadId: number,
    navigate: NavigateFunction,
    leadRepository: AwaitableRpcClient<LeadRepository>
  ): Promise<void> {
    if (!leadId) {
      return;
    }

    try {
      await leadRepository.deleteById(leadId);

      this.closeDelete();

      // Перенаправляем на список лидов
      navigate('/crm/leads');
    } catch (error) {
      logger.error('Failed to delete lead:', error);
      this.replaceState({
        error: 'Не удалось удалить лид',
      });
      this.notifyListeners();
    }
  }

  /**
   * Проверяет возможность архивирования лида.
   * Возвращает { allowed, message } — message содержит предупреждение или причину отказа.
   */
  async checkCanArchive(
    leadId: number,
    leadRepository: AwaitableRpcClient<LeadRepository>
  ): Promise<{ allowed: boolean; message?: string }> {
    try {
      return await leadRepository.canUpdate(leadId, 'is_archived');
    } catch (error) {
      logger.error('Failed to check archive permission:', error);
      return { allowed: false, message: extractErrorMessage(error, 'Не удалось проверить права') };
    }
  }

  /**
   * Архивировать лид. Вызывать после checkCanArchive + подтверждения пользователя.
   * Возвращает null при успехе или сообщение об ошибке.
   */
  async archiveLead(
    leadId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    leadRepository: AwaitableRpcClient<LeadRepository>,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<string | null> {
    try {
      await leadRepository.update(leadId, { is_archived: true } as Partial<Lead>);
      await this.reload(
        leadId,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      );
      return null;
    } catch (error) {
      logger.error('Failed to archive lead:', error);
      return extractErrorMessage(error, 'Не удалось архивировать лид');
    }
  }

  /**
   * Убрать лид из архива. Возвращает null при успехе или сообщение об ошибке.
   */
  async unarchiveLead(
    leadId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    leadRepository: AwaitableRpcClient<LeadRepository>,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<string | null> {
    try {
      await leadRepository.update(leadId, { is_archived: false } as Partial<Lead>);
      await this.reload(
        leadId,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      );
      return null;
    } catch (error) {
      logger.error('Failed to unarchive lead:', error);
      return extractErrorMessage(error, 'Не удалось убрать лид из архива');
    }
  }

  /**
   * Перезагрузить данные лида (для использования после успешного редактирования)
   */
  async reload(
    leadId: number,
    isModal: boolean,
    setTabTitle: (title: string) => void,
    navigate: NavigateFunction,
    leadRepository: AwaitableRpcClient<LeadRepository>,
    partnerRepository: AwaitableRpcClient<PartnerRepository>
  ): Promise<void> {
    await this.load(
      leadId,
      isModal,
      setTabTitle,
      navigate,
      leadRepository,
      partnerRepository
    );
  }
}
